import { FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { partnerApi } from '../../lib/partnerApi'
import { getApiError } from '../../lib/api'
import { buildReferralInviteUrl } from '../../services/referral'

export default function PartnerSignupPage() {
  const { t } = useTranslation('partners')
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState<'link' | 'code' | null>(null)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await partnerApi.post('/partners/signup', {
        email,
        first_name: firstName,
        last_name: lastName,
        password,
      })
      setCode(res.data.code)
    } catch (err) {
      setError(getApiError(err, t('signup.failed')))
    } finally {
      setLoading(false)
    }
  }

  const copyInviteLink = async () => {
    if (!code) return
    try {
      await navigator.clipboard.writeText(buildReferralInviteUrl(code))
      setCopied('link')
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setCopied(null)
    }
  }

  const copyCode = async () => {
    if (!code) return
    try {
      await navigator.clipboard.writeText(code)
      setCopied('code')
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setCopied(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-md mx-auto bg-white rounded-2xl border border-gray-200 p-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('signup.title')}</h1>
        <p className="mt-2 text-sm text-gray-500">{t('signup.subtitle')}</p>

        {code ? (
          <div className="mt-8 space-y-4">
            <p className="text-sm text-gray-600">{t('signup.success')}</p>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                {t('dashboard.inviteLink')}
              </p>
              <code className="block break-all rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
                {buildReferralInviteUrl(code)}
              </code>
              <button
                type="button"
                onClick={copyInviteLink}
                className="mt-2 w-full rounded-lg bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white hover:bg-[#e04e00]"
              >
                {copied === 'link' ? t('dashboard.copied') : t('dashboard.copyLink')}
              </button>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">{t('dashboard.yourCode')}</p>
              <div className="font-mono text-2xl font-bold tracking-wide text-gray-900">{code}</div>
              <button
                type="button"
                onClick={copyCode}
                className="mt-2 text-sm font-semibold text-[#FF5A00] hover:underline"
              >
                {copied === 'code' ? t('dashboard.copied') : t('dashboard.copyCode')}
              </button>
            </div>
            <Link
              to="/partners/dashboard"
              className="block text-center rounded-lg border border-gray-300 font-semibold py-2.5 hover:bg-gray-50"
            >
              {t('signup.goDashboard')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('signup.password')}
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              {t('signup.firstName')}
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              {t('signup.lastName')}
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              {t('signup.email')}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-[#FF5A00] text-white font-semibold py-2.5 hover:bg-[#e04e00] disabled:opacity-50"
            >
              {loading ? t('signup.submitting') : t('signup.submit')}
            </button>
            <p className="text-center text-sm text-gray-500">
              {t('signup.haveAccount')}{' '}
              <Link to="/partners/dashboard" className="text-[#FF5A00] font-medium hover:underline">
                {t('signup.loginLink')}
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
